import React, { ReactElement } from 'react'
import { Link } from 'react-router-dom'
import { Cell, Row } from 'react-table'
import styled from 'styled-components'
import dayjs from 'dayjs'
import {
  Box,
  Dejargonator,
  get,
  Link as MuiLink,
  TableColumn,
  TextBody,
} from '@ally/metronome-ui'
import { Account } from '@ally/data-types-afg-accounts'
import { asCurrency, maskAccountNumber } from '@ally/aos-utils'
import { AccountDetailsLink, TextBodyOverride } from '../Shared'
import {
  calculateRowTotal,
  isAutoAccountInGoodStanding,
  isAutoPayVehicleAccount,
  isAutoAccountPastDue,
  isOneTimePayExclusionVehicleAccount,
  isAutoAccountWithNoActiveVehicle,
  isAutoPayExclusionVehicleAccount,
} from './utils'
import CallUsModal from './CallUsModal'

interface GetColumnsProps {
  isDefaultView: boolean
}

interface CellProps {
  cell: Cell<Account>
  row: Row<Account>
}

interface FooterProps {
  rows: Row<Account>[]
}

const StyledDejarg = styled(Dejargonator)`
  text-align: left;
  white-space: normal;
  text-transform: none;
`
const StyledDejargContent = styled(TextBody)`
  text-transform: capitalize;
  font-size: ${get.fontSizes('sm')};
  border-bottom: 1px dotted ${get.colors('slate-3')};
`

const ErrorSpan = styled.span`
  color: ${get.colors('error')};
  font-weight: bold;
`

const getRowAction = (account: Account): ReactElement | null => {
  if (
    isOneTimePayExclusionVehicleAccount(account) ||
    isAutoAccountWithNoActiveVehicle(account) ||
    isAutoPayExclusionVehicleAccount(account)
  ) {
    return <CallUsModal />
  }
  if (isAutoAccountInGoodStanding(account) || isAutoAccountPastDue(account)) {
    return (
      <MuiLink text="Make a Payment">
        <Link to={`/sso/auto/payments?id=${account.id}`} />
      </MuiLink>
    )
  }
  if (isAutoPayVehicleAccount(account)) {
    return (
      <MuiLink text="Edit Auto Pay">
        <Link to={`/sso/auto/payments/enroll-autopay?id=${account.id}`} />
      </MuiLink>
    )
  }
  return <CallUsModal />
}

export const getColumns = ({
  isDefaultView,
}: GetColumnsProps): TableColumn<Account>[] => {
  const columns = {
    // column 1
    accountName: {
      id: 'name',
      accessor: (account: Account): string => {
        return account.nickName || account.name
      },
      Header: 'Vehicle Financing',
      Footer: 'Total',
      alignment: 'left',
      isSortable: true,
      Cell: ({
        cell,
        row: { original: account },
      }: CellProps): React.ReactNode | string => {
        return (
          <>
            <AccountDetailsLink
              text={account.nickName || account.name}
              account={account}
              key={account.id}
              allytm={`auto-autoAccount-${account.type}`}
              showAsText={isAutoAccountWithNoActiveVehicle(account)}
            />
            <Box
              display="inline-block"
              verticalAlign="middle"
              aria-hidden="true"
            >
              {maskAccountNumber(account.accountNumber)}
            </Box>
          </>
        )
      },
    },

    // column 2
    vehicleBalance: {
      id: 'vehicleBalance',
      accessor: 'balance.total',
      isSortable: true,
      Header: (
        <StyledDejarg
          trigger={{
            variant: 'element',
            content: (
              <StyledDejargContent tag="p" size="xs" weight="bold">
                Vehicle Balance
              </StyledDejargContent>
            ),
            ariaLabel: 'Select to learn more about your Vehicle Balance',
          }}
          ariaLabelPopover="Loan"
          body={
            <TextBodyOverride tag="p" size="sm" color="gray-400">
              This amount does not include finance charges and other unpaid
              amounts. To pay off your vehicle, select your vehicle and choose{' '}
              <TextBodyOverride
                tag="span"
                size="sm"
                weight="bold"
                color="gray-400"
              >
                Request a Quote{' '}
              </TextBodyOverride>
              under{' '}
              <TextBodyOverride
                tag="span"
                size="sm"
                weight="bold"
                color="gray-400"
              >
                Manage Account.
              </TextBodyOverride>
            </TextBodyOverride>
          }
        />
      ),
      alignment: 'right',
      Cell: ({ cell, row }: CellProps): React.ReactNode | string => {
        return isAutoAccountWithNoActiveVehicle(row.original)
          ? 'Unavailable'
          : asCurrency(row.original.balance?.total)
      },
      Footer: ({ rows }: FooterProps): React.ReactNode => {
        const total = React.useMemo(
          () => calculateRowTotal(rows, 'vehicleBalance'),
          [rows],
        )
        return <>{total}</>
      },
    },

    // column 3
    paymentDue: {
      id: 'paymentDue',
      Header: 'Payment Due',
      alignment: 'right',
      isSortable: true,
      accessor: 'meta.nextPayment.totalAmountDue',
      Cell: ({ cell, row }: CellProps): React.ReactNode | string => {
        return isAutoAccountWithNoActiveVehicle(row.original)
          ? 'Unavailable'
          : asCurrency(row.original.meta?.nextPayment?.totalAmountDue)
      },
      Footer: ({ rows }: FooterProps): React.ReactNode => {
        const total = React.useMemo(
          () => calculateRowTotal(rows, 'paymentDue'),
          [rows],
        )
        return <>{total}</>
      },
    },

    // column 4
    dueDate: {
      id: 'dueDate',
      Header: 'Due Date',
      alignment: 'right',
      isSortable: true,
      accessor: 'meta.nextPayment.dueDate',
      Cell: ({
        cell,
        row: { original: account },
      }: CellProps): React.ReactNode => {
        const dueDate = account.meta?.nextPayment?.dueDate
        const pastDue = isAutoAccountPastDue(account)
        const autoAccountWithNoActiveVehicle = isAutoAccountWithNoActiveVehicle(
          account,
        )

        if (!autoAccountWithNoActiveVehicle && dueDate && !pastDue) {
          return <span>{dayjs(dueDate).format('MMM DD, YYYY')}</span>
        }
        if (!autoAccountWithNoActiveVehicle && dueDate && pastDue) {
          return <ErrorSpan>{dayjs(dueDate).format('MMM DD, YYYY')}</ErrorSpan>
        }
        if (autoAccountWithNoActiveVehicle) {
          return 'Unavailable'
        }
        return null
      },
    },

    // column 5
    actions: {
      id: 'actions',
      Header: 'Actions',
      alignment: 'right',
      Cell: ({
        cell,
        row: { original: account },
      }: CellProps): React.ReactElement<Element> | null =>
        getRowAction(account),
    },
  }

  const tileViewCols = [
    columns.accountName,
    columns.vehicleBalance,
    columns.paymentDue,
    columns.actions,
  ] as TableColumn<Account>[]
  const defaultViewCols = [
    columns.accountName,
    columns.vehicleBalance,
    columns.paymentDue,
    columns.dueDate,
    columns.actions,
  ] as TableColumn<Account>[]

  return isDefaultView ? defaultViewCols : tileViewCols
}
